"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _Platform = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Platform"));
var _StyleSheet = _interopRequireDefault(require("react-native-web/dist/cjs/exports/StyleSheet"));
var _View = _interopRequireDefault(require("react-native-web/dist/cjs/exports/View"));
var _InputLabel = _interopRequireDefault(require("../InputLabel"));
var _ButtonBase = _interopRequireDefault(require("../Button/ButtonBase"));
var _StackView = _interopRequireDefault(require("../StackView"));
var _ThemeProvider = require("../ThemeProvider");
var _utils = require("../utils");
var _input = require("../utils/input");
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const [selectProps, selectedSystemPropTypes] = (0, _utils.selectSystemProps)([_utils.a11yProps, _utils.focusHandlerProps, _utils.pressProps, _utils.viewProps]);

// We need to drop the icon before passing it to the `ButtonBase`, because it's
// being handled separately in this case
const selectButtonTokens = (_ref, _ref2) => {
  let {
    icon: _,
    ...tokens
  } = _ref;
  let {
    trackHeight,
    outerBorderGapButton
  } = _ref2;
  return (0, _utils.selectTokens)('Button', {
    ...tokens,
    // Width tokens are applied to our inner track. Disable Button width token so it wraps our track width.
    outerBorderGap: outerBorderGapButton,
    height: trackHeight,
    width: null
  });
};

// Map and rename icon-specific tokens to name used within Icon
const selectIconTokens = _ref3 => {
  let {
    iconSize,
    iconColor
  } = _ref3;
  return {
    size: iconSize,
    color: iconColor
  };
};
const selectTrackSwitchStyles = _ref4 => {
  let {
    switchSize,
    width,
    trackHeight
  } = _ref4;
  return {
    width,
    height: Math.max(switchSize, trackHeight)
  };
};
const selectTrackStyles = _ref5 => {
  let {
    trackBorderWidth,
    trackBorderColor,
    trackBorderRadius,
    width
  } = _ref5;
  return {
    borderWidth: trackBorderWidth,
    borderColor: trackBorderColor,
    borderRadius: trackBorderRadius,
    width
  };
};
const selectSwitchStyles = _ref6 => {
  let {
    switchSize,
    switchColor,
    switchBorderWidth,
    switchBorderColor,
    switchBorderRadius,
    switchShadow
  } = _ref6;
  return {
    position: 'absolute',
    width: switchSize,
    height: switchSize,
    backgroundColor: switchColor,
    borderWidth: switchBorderWidth,
    borderColor: switchBorderColor,
    borderRadius: switchBorderRadius,
    ...(0, _ThemeProvider.applyShadowToken)(switchShadow),
    ..._Platform.default.select({
      // TODO: https://github.com/telus/universal-design-system/issues/487
      web: {
        transition: 'transform 200ms'
      }
    })
  };
};
const selectMobileTokens = tokens => ({
  ..._Platform.default.select({
    web: {},
    default: {
      switchSize: tokens?.mobileSwitchSize,
      trackHeight: tokens?.mobileTrackHeight,
      width: tokens?.mobileWidth,
      trackBorderWidth: tokens?.mobileTrackBorderWidth
    }
  })
});
const selectLabelStyles = _ref7 => {
  let {
    labelMarginLeft
  } = _ref7;
  return {
    marginLeft: labelMarginLeft
  };
};
const selectLabelTokens = _ref8 => {
  let {
    labelColor,
    labelFontName,
    labelFontSize,
    labelFontWeight,
    labelLineHeight
  } = _ref8;
  return {
    color: labelColor,
    fontName: labelFontName,
    fontWeight: labelFontWeight,
    fontSize: labelFontSize,
    lineHeight: labelLineHeight
  };
};
const ToggleSwitch = /*#__PURE__*/_react.default.forwardRef((_ref9, ref) => {
  let {
    copy = 'en',
    value,
    initialValue,
    onChange,
    id,
    label,
    inactive,
    tokens,
    tooltip,
    variant,
    accessibilityRole = 'switch',
    accessibilityLabel = label,
    togglePosition = 'end',
    ...rest
  } = _ref9;
  const getTokens = (0, _ThemeProvider.useThemeTokensCallback)('ToggleSwitch', tokens, variant);
  const themeTokens = getTokens();
  const {
    currentValue,
    setValue
  } = (0, _input.useInputValue)({
    value,
    initialValue,
    onChange
  });
  const handlePress = event => setValue(!currentValue, event);
  const getButtonTokens = buttonState => selectButtonTokens(getTokens(buttonState, selectMobileTokens(themeTokens)), getTokens(themeTokens, selectMobileTokens(themeTokens)));
  const uniqueId = (0, _utils.useUniqueId)('toggleSwitch');
  const inputId = id ?? uniqueId;
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_StackView.default, {
    space: themeTokens.space,
    direction: togglePosition === 'start' ? 'row-reverse' : 'row',
    tokens: {
      alignItems: 'center',
      justifyContent: themeTokens.containerWidth ? 'space-between' : 'start',
      width: themeTokens.containerWidth
    },
    children: [Boolean(label) && /*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
      style: [selectLabelStyles(themeTokens), staticStyles.containText],
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_InputLabel.default, {
        copy: copy,
        forId: inputId,
        label: label,
        tokens: selectLabelTokens(themeTokens),
        tooltip: tooltip
      })
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_ButtonBase.default, {
      id: id,
      ref: ref,
      selected: currentValue,
      inactive: inactive,
      tokens: getButtonTokens,
      accessibilityLabel: accessibilityLabel,
      accessibilityRole: accessibilityRole,
      accessibilityState: {
        checked: currentValue
      },
      onPress: handlePress,
      ...selectProps(rest),
      children: buttonState => {
        const stateTokens = getTokens(buttonState, selectMobileTokens(themeTokens));
        const IconComponent = stateTokens.icon;
        const switchStyles = selectSwitchStyles(stateTokens);
        const trackStyles = selectTrackStyles(stateTokens);
        const iconTokens = selectIconTokens(stateTokens);
        const trackSwitchStyles = selectTrackSwitchStyles(stateTokens);
        const {
          switchSize,
          trackHeight,
          width,
          trackBorderWidth
        } = stateTokens;
        // If drag-slide support is needed, use a PanResponder and apply these to an Animated value.
        // Use translate transforms for smoothest non-thread-blocking animations and to allow drag.
        // Two different translates depending on the switchSize and trackSize relationship.
        const isSwitchTallerThanTrack = switchSize >= trackHeight;
        const slideStart = isSwitchTallerThanTrack ? 0 : trackBorderWidth;
        const slideEnd = isSwitchTallerThanTrack ? width - switchSize : width - switchSize - trackBorderWidth;
        const switchOffset = buttonState.selected ? slideEnd : slideStart;
        const switchPositionStyle = {
          transform: [{
            translateX: switchOffset
          }]
        };
        return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_View.default, {
          nativeID: "trackSwitch",
          style: [trackSwitchStyles, staticStyles.trackSwitch],
          children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
            style: [staticStyles.track, trackStyles]
          }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
            style: [staticStyles.switch, switchStyles, switchPositionStyle],
            children: IconComponent && /*#__PURE__*/(0, _jsxRuntime.jsx)(IconComponent, {
              ...iconTokens
            })
          })]
        });
      }
    })]
  });
});
ToggleSwitch.displayName = 'ToggleSwitch';
ToggleSwitch.propTypes = {
  ...selectedSystemPropTypes,
  /**
   * Whether the English or French copy will be used (e.g. for accessibility labels).
   */
  copy: _propTypes.default.oneOf(['en', 'fr']),
  tokens: (0, _utils.getTokensPropType)('ToggleSwitch'),
  variant: _utils.variantProp.propType,
  /**
   * If this is a "controlled" component and a parent handles its selected state,
   * tells the switch if it is on (true) or off (false).
   */
  value: _propTypes.default.bool,
  /**
   * If this is an "uncontrolled" component that handles its own selected state,
   * optionally tells the switch if it should default to on (true) or off (false).
   */
  initialValue: _propTypes.default.bool,
  /**
   * Function called when the switch is toggled, with one boolean argument that is the
   * new position of the switch, on (true) or off (false). For "controlled" components,
   * this should always be passed and used to control the state of the switch.
   */
  onChange: _propTypes.default.func,
  /**
   * Input ID.
   */
  id: _propTypes.default.string,
  /**
   * An optional label.
   */
  label: _propTypes.default.string,
  /**
   * If passed, the switch does not respond to user input and may recieve different
   * theme tokens if the theme supports inactive appearance.
   */
  inactive: _propTypes.default.bool,
  /**
   * Content of an optional Tooltip. If set, a tooltip button will be shown next to the label.
   */
  tooltip: _propTypes.default.string,
  /**
   * Controls the position of the switch in relation to the label.
   * - 'start': switch on the start, before the `label` and `tooltip`.
   * - 'end': switch to the end, after the `label` and `tooltip` (default).
   */
  togglePosition: _propTypes.default.oneOf(['start', 'end'])
};
const staticStyles = _StyleSheet.default.create({
  track: {
    flexDirection: 'row'
  },
  switch: {
    alignItems: 'center',
    justifyContent: 'center'
  },
  containText: {
    flexShrink: 1
  },
  trackSwitch: {
    flexDirection: 'row',
    alignItems: 'center'
  }
});
var _default = exports.default = ToggleSwitch;